/**
 * Ingresso dopo l'accesso e scelta del contesto (V05).
 *
 * Il Super Admin senza societa entra dall'elenco delle societa (V54), perche
 * non ha squadre da scegliere. Con una sola squadra-stagione nella stagione
 * corrente la scelta si salta.
 * Il salto avviene solo all'ingresso: aperta dal menu, la vista mostra
 * sempre l'elenco, perche chi la apre cerca le stagioni passate.
 *
 * Senza squadre nella stagione corrente l'Admin trova il percorso guidato:
 * stagione, squadre, incroci. Ogni passo rimanda alla sua vista, da cui si
 * torna qui con «Torna alle squadre»; le spunte si ricalcolano dai dati a
 * ogni apertura, e cosi il percorso riprende da dove ci si era fermati.
 */

import { Link, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import type { Matrice } from '../amministrazione/matrice.js';
import { ElencoPassi } from '../comuni/ElencoPassi.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { useDati } from '../comuni/dati.js';
import { type SquadraStagione, useContesto } from '../navigazione/contesto.js';
import { voceAperta } from '../navigazione/voci.js';
import { type ChiavePassoSocieta, percorsoSocieta } from './percorso.js';

export function Ingresso() {
  const { utente, squadre, erroreSquadre } = useContesto();
  if (utente.superAdmin && !utente.societaId) return <Navigate to="/piattaforma/societa" replace />;
  if (!squadre) return <p className="attesa">{erroreSquadre ?? 'Carico…'}</p>;

  const correnti = squadre.filter((s) => s.stagioneCorrente);
  const unica = correnti.length === 1 ? correnti[0] : undefined;
  return <Navigate to={unica ? `/sq/${unica.id}` : '/squadre'} replace />;
}

export function SceltaContesto() {
  const { utente, squadre, erroreSquadre, esci } = useContesto();
  if (utente.superAdmin && !utente.societaId) return <Navigate to="/piattaforma/societa" replace />;
  if (!squadre) return <p className="attesa">{erroreSquadre ?? 'Carico…'}</p>;

  const correnti = squadre.filter((s) => s.stagioneCorrente);
  const passate = squadre.filter((s) => !s.stagioneCorrente);
  const guidato = correnti.length === 0 && amministra(utente);

  return (
    <div className="mx-auto min-h-dvh max-w-2xl px-4 pt-[max(16px,env(safe-area-inset-top))] pb-[max(16px,env(safe-area-inset-bottom))]">
      <header className="flex items-center justify-between gap-3 border-b-2 border-foreground pb-2">
        <h1 className="text-[22px] font-bold">Scegli la squadra</h1>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={() => void esci()}
        >
          Esci
        </Button>
      </header>

      {guidato && <PercorsoGuidato />}
      {correnti.length === 0 && !guidato && (
        <p className="my-4 text-muted-foreground">{senzaSquadre(utente)}</p>
      )}
      {correnti.length === 0 && utente.superAdmin && (
        <Button asChild variant={guidato ? 'outline' : 'default'} className="mb-4">
          <Link to="/piattaforma/societa">Vai alla piattaforma</Link>
        </Button>
      )}
      <Schede squadre={correnti} />

      {passate.length > 0 && (
        <details className="mt-6">
          <summary className="flex min-h-12 cursor-pointer items-center font-semibold text-muted-foreground">
            Stagioni passate · {passate.length}
          </summary>
          <Schede squadre={passate} />
        </details>
      )}
    </div>
  );
}

function Schede({ squadre }: { squadre: SquadraStagione[] }) {
  if (squadre.length === 0) return null;
  return (
    <ul>
      {squadre.map((s) => (
        <li key={s.id}>
          <Link
            to={`/sq/${s.id}`}
            className="flex min-h-16 flex-col justify-center gap-1 border-b border-border py-2.5"
          >
            <strong className="text-lg">{s.squadra}</strong>
            <span className="text-sm text-muted-foreground tabular-nums">
              {s.stagione}
              {s.categoria && s.categoria !== s.squadra && ` · ${s.categoria}`} · {s.tesserati}{' '}
              tesserati
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * Il percorso legge la matrice di V48, che richiede la gestione delle squadre,
 * e rimanda alle stagioni: servono entrambe le voci.
 */
const amministra = (utente: ReturnType<typeof useContesto>['utente']): boolean =>
  voceAperta('/amministrazione/stagioni', utente) !== undefined &&
  voceAperta('/amministrazione/squadre-stagione', utente) !== undefined;

const PASSI: Record<
  ChiavePassoSocieta,
  { titolo: string; testo: string; azioni: { etichetta: string; a: string }[] }
> = {
  stagione: {
    titolo: 'Crea la stagione',
    testo:
      'Per esempio 2026/27: vale per tutte le squadre della società. La prima stagione diventa subito quella corrente.',
    azioni: [{ etichetta: 'Vai alle stagioni', a: '/amministrazione/stagioni' }],
  },
  squadre: {
    titolo: 'Crea le squadre',
    testo:
      "Contenitori che durano negli anni, come «Allievi» o «Prima squadra», senza l'anno nel nome.",
    azioni: [{ etichetta: 'Vai alle squadre', a: '/amministrazione/squadre' }],
  },
  incroci: {
    titolo: 'Aggiungi le squadre alla stagione',
    testo:
      'Ogni incrocio tra squadra e stagione ha la sua rosa, il suo staff e le sue partite: da lì il cruscotto guida il resto.',
    azioni: [{ etichetta: 'Vai alle squadre-stagione', a: '/amministrazione/squadre-stagione' }],
  },
};

/** Admin senza squadre nella stagione corrente: i passi per partire, spuntati dai dati. */
function PercorsoGuidato() {
  const { dati: matrice, errore } = useDati<Matrice>('/amministrazione/squadre-stagione');
  if (!matrice) {
    return (
      <div className="my-4">
        <StatoCaricamento errore={errore} />
      </div>
    );
  }

  const { passi, stagioneDaImpostare } = percorsoSocieta(matrice);
  return (
    <section className="my-4">
      <p className="mb-4 text-muted-foreground">
        {matrice.celle.length === 0
          ? 'La società è ancora da preparare: tre passi e le squadre compaiono qui.'
          : 'La stagione corrente non ha ancora squadre: quando le aggiungi compaiono qui.'}
      </p>
      <ElencoPassi
        passi={passi.map((p) => ({
          ...p,
          ...(p.chiave === 'stagione' && stagioneDaImpostare
            ? {
                ...PASSI.stagione,
                titolo: 'Imposta la stagione corrente',
                testo: 'Nessuna stagione è corrente: scegli quella in corso, o creane una nuova.',
              }
            : PASSI[p.chiave]),
        }))}
      />
    </section>
  );
}

/** Chi non amministra, o il Super Admin senza ruoli nella societa. */
function senzaSquadre(utente: ReturnType<typeof useContesto>['utente']): string {
  if (utente.superAdmin && utente.assegnazioni.length === 0) {
    return 'Nella società non hai ruoli: la tua area è la piattaforma.';
  }
  return "Nessuna squadra assegnata nella stagione corrente. Contatta l'Admin Squadra della tua società.";
}
